"use client"

import useIsMobile from "@/(admin)/modules/ui-builder/config/lib/use-is-mobile"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { t } from "@/languages/i18n"
import { buildContactEmailHtml } from "@/lib/email-templates/contact"
import { getSettings } from "@/lib/navigation-services"
import { DataSettingItem } from "@/types/types"
import { ComponentConfig } from "@measured/puck"
import { Form, Select, Spin } from "antd"
import { CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js"
import { useEffect, useMemo, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface ContactItem {
  icon?: string
  itemTitle?: string
  itemContent?: string
}

export interface ContactProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  gapCustom?: string
  heightContact?: string
  title?: string
  subTitle?: string
  inPopup?: boolean
  contactItem: ContactItem[]
  messageSuccess?: string
  messageFail?: string
}

export const RenderConfig: ComponentConfig<ContactProps> = {
  render: ({ className, type, styles, gapCustom, responsiveType, heightContact, title, subTitle, contactItem }) => {
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")
    const [isPopupVisible, setIsPopupVisible] = useState(false)
    const isMobile = useIsMobile()
    const [selectedCountry, setSelectedCountry] = useState<string>("VN")
    const countries = useMemo(() => getCountries(), [])
    const [formData, setFormData] = useState({
      emailTo: "",
      yourName: "",
      email: "",
      phone: "",
      whatsapp: "",
      message: "",
    })

    const fetchSetting = async () => {
      try {
        const response = await getSettings()
        const emailTo = response.find((item: DataSettingItem) => item.attributes.key === "emailForm")?.attributes.value
        setFormData(prev => ({ ...prev, emailTo: emailTo }))
      } catch (error) {
        console.error("Error fetching setting data:", error)
      }
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      try {
        await form.validateFields()
      } catch (error) {
        return
      }

      setIsLoading(true)
      try {
        const timestamp = new Date()
        const pad = (n: number) => String(n).padStart(2, "0")
        const stamp = `${timestamp.getFullYear()}${pad(timestamp.getMonth() + 1)}${pad(timestamp.getDate())}-${pad(
          timestamp.getHours(),
        )}${pad(timestamp.getMinutes())}${pad(timestamp.getSeconds())}`
        const shortId = Math.random().toString(36).slice(2, 6).toUpperCase()
        const uniqueSubject = `[Thông báo] Liên hệ - ${formData.yourName || "Khách"} - ${stamp}-${shortId}`

        const html = buildContactEmailHtml({
          yourName: formData.yourName,
          email: formData.email,
          phone: formData.phone,
          whatsapp: formData.whatsapp,
          message: formData.message,
          countryCode: selectedCountry,
          callingCode: String(getCountryCallingCode(selectedCountry as CountryCode)),
        })

        const response = await fetch("/api/email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: uniqueSubject,
            emailTo: formData.emailTo,
            content: `Tên: ${formData.yourName} - Email: ${formData.email} - SĐT: +${getCountryCallingCode(
              selectedCountry as CountryCode,
            )} ${formData.phone}${formData.whatsapp ? ` - WhatsApp: ${formData.whatsapp}` : ""}`,
            html,
          }),
        })

        if (!response.ok) {
          throw new Error("Failed to submit data")
        }
        setIsPopupVisible(true)
        setFormData({
          emailTo: "",
          yourName: "",
          email: "",
          phone: "",
          whatsapp: "",
          message: "",
        })
        form.resetFields()
        setMessage("Đăng ký tư vấn thành công!")
      } catch (error) {
        setMessage("Đã xảy ra lỗi khi đăng ký!")
      } finally {
        setIsLoading(false)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    const closePopup = () => {
      setIsPopupVisible(false)
      form.resetFields()
    }

    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    useEffect(() => {
      fetchSetting()
    }, [])

    return (
      <>
        {responsiveCSS}
        <style
          dangerouslySetInnerHTML={{
            __html: `
            #${id} .ant-select-selector {
              border-color: #00000014 !important;
            }
            #${id} .ant-select-selector:hover {
              border-color: #00000014 !important;
            }
            #${id} .ant-select-focused .ant-select-selector {
              border-color: #00000014 !important;
            }
          `,
          }}
        />
        <div id={id} className={twMerge(className, `w-full flex lg:flex-row flex-col justify-between gap-10 h-full `)}>
          <div className="max-w-[463px] flex flex-col lg:items-start items-center gap-10 lg:gap-16 justify-between">
            {title && (
              <h2 className="font-bold text-[26px] leading-[34px] lg:leading-[54px] lg:text-[40px] lg:text-left text-center text-[#0C9443]">
                {title}
              </h2>
            )}
            {/* {subTitle && <p>{subTitle}</p>} */}
            <div className={`flex flex-col lg:gap-10 gap-4 lg:max-w-[418px] max-w-[311px]`}>
              {contactItem.map((item, index) => {
                return (
                  <div key={index} className="flex flex-row items-start gap-2">
                    {item.icon && (
                      <img src={`${ADMIN_API_CONFIG.API_URL}${item.icon}`} alt={item.itemTitle} className="h-6 w-6" />
                    )}
                    <div className="flex flex-col gap-1">
                      {item.itemTitle && (
                        <p className="font-bold text-base leading-[27px] text-[#242F3E]">{item.itemTitle}</p>
                      )}
                      {item.itemContent && (
                        <p className="text-[#4F4F4F] text-base leading-[27px] font-normal">{item.itemContent}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <div
            className={`${
              (title || subTitle) && !isMobile ? "pt-[47px]" : ""
            } w-full items-center h-full max-w-[463px]`}
          >
            <form className={`flex flex-col gap-4 `} onSubmit={handleSubmit}>
              <input
                name="yourName"
                type="text"
                className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93] text-base leading-[27px]"
                placeholder={t("Your name")}
                onChange={handleInputChange}
                required
                style={{ maxHeight: "48px" }}
              />
              <input
                name="email"
                type="email"
                className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93] text-base leading-[27px]"
                placeholder={t("Email")}
                onChange={handleInputChange}
                required
                style={{ maxHeight: "48px" }}
              />
              <div className="flex items-center gap-2">
                <Select
                  value={selectedCountry}
                  onChange={value => setSelectedCountry(value)}
                  className="[&_.ant-select-selector]:py-3 [&_.ant-select-selector]:px-4 [&_.ant-select-selector]:bg-white [&_.ant-select-selector]:rounded-xl [&_.ant-select-selector]:border [&_.ant-select-selector]:border-[#00000014] [&_.ant-select-selector]:text-base [&_.ant-select-selector]:leading-[27px] [&_.ant-select-selector]:h-full"
                  style={{ height: "48px", width: "100%" }}
                  virtual={false}
                >
                  {countries.map(code => (
                    <Select.Option key={code} value={code}>
                      {code} (+{getCountryCallingCode(code)})
                    </Select.Option>
                  ))}
                </Select>
                <input
                  name="phone"
                  type="tel"
                  className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93] text-base leading-[27px]"
                  placeholder={t("Phone")}
                  onChange={handleInputChange}
                  required
                  style={{ maxHeight: "48px" }}
                />
              </div>
              <input
                name="whatsapp"
                type="tel"
                className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93] text-base leading-[27px]"
                placeholder={t("Enter your WhatsApp number")}
                onChange={handleInputChange}
                style={{ maxHeight: "48px" }}
              />
              <textarea
                name="message"
                className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full resize-none placeholder-[#8E8E93] text-base leading-[27px]"
                placeholder={t("Message")}
                onChange={handleInputChange}
                style={{ height: "6rem" }}
              ></textarea>
              <button
                type="submit"
                className="bg-[#0B9444] rounded-xl w-full flex flex-row items-center justify-center gap-2 py-[13px]"
              >
                <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.7916 8.9502L8.85059 11.8912"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.4419 4.68417L13.5049 16.4942C13.2039 17.3972 11.9729 17.5112 11.5109 16.6792L8.85095 11.8912L4.06294 9.23117C3.23094 8.76917 3.34494 7.53817 4.24794 7.23717L16.0579 3.30017C16.9129 3.01517 17.7269 3.82917 17.4419 4.68417Z"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-white font-semibold text-base leading-[22px]" style={{ lineHeight: "22px" }}>
                  {t("Send Us")}
                </p>
              </button>
              {isLoading && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <Spin />
                </div>
              )}

              {isPopupVisible && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="bg-white p-8 rounded shadow-md relative">
                    <button
                      onClick={closePopup}
                      className="absolute top-0 text-[30px] right-2 text-gray-600 hover:text-gray-800"
                      aria-label="Close"
                    >
                      &times;
                    </button>
                    <p className="text-lg font-semibold">{message}</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </>
    )
  },
}
